// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/ConnectorDefinitionsPanel.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaPlus,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaLink,
  FaUnlink,
} from "react-icons/fa";
import { BiSolidEdit } from "react-icons/bi";
import { RiDeleteBin6Line } from "react-icons/ri";

import ReusablePopup from "../../../../ReusablePopup";
import ConnectorDefinitionModal from "./ConnectorDefinitionModal";
import {
  listDefinitions,
  createOrUpdateDefinition,
  deleteDefinition,
} from "./utils/connectorDefinitionsApi";
import { listOperations } from "./utils/operationsApi";
import { listConnections } from "./utils/oauthApi";
import styles from "./ConnectorDefinitionsPanel.module.css";

import ScopeTabs from "./ScopeTabs";
import ResourceScopeBadge from "./utils/ResourceScopeBadge";
import {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
} from "./utils/scopeAttachmentsApi";
import {
  classifyGuardRefusal,
  formatAttachedAppChips,
  formatResourceInUseError,
} from "./utils/scopeAttachmentValidation";

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

function formatBlockingRefs(blockingRefs) {
  if (!blockingRefs || typeof blockingRefs !== "object") return "";

  const chains = Array.isArray(blockingRefs.chains) ? blockingRefs.chains : [];
  const forms = Array.isArray(blockingRefs.leadgen_forms)
    ? blockingRefs.leadgen_forms
    : [];

  if (chains.length === 0 && forms.length === 0) return "";

  const parts = [];

  if (chains.length > 0) {
    const lines = chains.map((c) => {
      const status = c.enabled === false ? "disabled" : "enabled";
      const label = c.name || c.chain_id || "(unnamed chain)";
      const where = c.where ? ` — ${c.where}` : "";
      return `  • ${label} (${status})${where}`;
    });
    parts.push(
      `Used by ${chains.length} chain${chains.length === 1 ? "" : "s"}:\n${lines.join("\n")}`,
    );
  }

  if (forms.length > 0) {
    const lines = forms.map((f) => {
      const label = f.form_name || f.form_uuid || "(unnamed form)";
      const app = f.app_name ? ` (app: ${f.app_name})` : "";
      return `  • ${label}${app}`;
    });
    parts.push(
      `Used by ${forms.length} leadgen form${forms.length === 1 ? "" : "s"}:\n${lines.join("\n")}`,
    );
  }

  parts.push(
    "To delete this action, first remove it from those chain nodes and leadgen forms.",
  );

  return parts.join("\n\n");
}

const ConnectorDefinitionsPanel = ({ appData, controlledScope, hideScopeTabs }) => {
  const region = appData?.region?.toLowerCase();
  const appName = appData?.app_name;

  const isGlobalContext = !appName;

  const [internalTab, setInternalTab] = useState(SCOPE.TENANT);
  const activeTab = controlledScope || internalTab;

  const [tenantDefinitions, setTenantDefinitions] = useState([]);
  const [appDefinitions, setAppDefinitions] = useState([]);
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [attachmentsByDefinition, setAttachmentsByDefinition] = useState(
    () => new Map(),
  );

  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const [connectedProviders, setConnectedProviders] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);

  const [popup, setPopup] = useState({
    isOpen: false,
    heading: "",
    subtext: "",
    paragraph: "",
    confirmText: "OK",
    cancelText: "",
    icon: null,
    iconColor: "#16a34a",
    iconBG: "#dcfce7",
    onConfirm: null,
  });

  const closePopup = () =>
    setPopup((p) => ({ ...p, isOpen: false, onConfirm: null }));

  const openAlert = ({
    heading,
    subtext = "",
    paragraph = "",
    icon = null,
    iconColor = "#16a34a",
    iconBG = "#dcfce7",
  }) => {
    setPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText: "OK",
      cancelText: "",
      icon,
      iconColor,
      iconBG,
      onConfirm: closePopup,
    });
  };

  const openConfirm = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = null,
    iconColor = "#dc2626",
    iconBG = "#fee2e2",
    onConfirm,
  }) => {
    setPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText,
      icon,
      iconColor,
      iconBG,
      onConfirm,
    });
  };

  const openGuardPopup = ({ refusal }) => {
    const BodyNode = (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        {refusal.kind === "attached-in-use" && (
          <>
            <div style={{ marginBottom: 8 }}>{refusal.message}</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {formatAttachedAppChips(
                refusal.apps.map((a) => ({ app_name: a })),
              ).map((chip) => (
                <button
                  key={chip.app_name}
                  type="button"
                  onClick={async () => {
                    try {
                      await detachScope({
                        region,
                        appName: chip.app_name,
                        resourceType: "connector_definition",
                        tenantResourceId: refusal.resourceId,
                      });
                      openAlert({
                        heading: "Detached",
                        paragraph: `${chip.app_name} has been detached from this action.`,
                        icon: <FaCheckCircle size={28} />,
                      });
                      closePopup();
                      setRefreshTick((t) => t + 1);
                    } catch (e) {
                      openAlert({
                        heading: "Couldn't detach",
                        paragraph: e.message,
                        icon: <FaTimesCircle size={28} />,
                        iconColor: "#dc2626",
                        iconBG: "#fee2e2",
                      });
                    }
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #c4b5fd",
                    background: "#f5f3ff",
                    color: "#6b46c1",
                    cursor: "pointer",
                  }}
                >
                  Detach {chip.app_name}
                </button>
              ))}
            </div>
          </>
        )}
        {refusal.kind === "referenced" && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              margin: 0,
              fontSize: 13,
            }}
          >
            {formatResourceInUseError({
              blocking_refs: refusal.refs,
              error: refusal.message,
            }) || formatBlockingRefs(refusal.refs)}
          </pre>
        )}
        {refusal.kind !== "attached-in-use" &&
          refusal.kind !== "referenced" && <div>{refusal.message}</div>}
      </div>
    );

    setPopup({
      isOpen: true,
      heading:
        refusal.kind === "attached-in-use"
          ? "This action is attached to other apps"
          : refusal.kind === "referenced"
            ? "This action is in use"
            : "Couldn't complete the request",
      subtext: "",
      paragraph: BodyNode,
      confirmText: "OK",
      cancelText: "",
      icon:
        refusal.kind === "attached-in-use" ? (
          <FaExclamationTriangle size={28} />
        ) : (
          <FaTimesCircle size={28} />
        ),
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: closePopup,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!region) {
        setLoading(false);
        return;
      }
      if (!appName && !isGlobalContext) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // ⭐ Two parallel calls:
        // 1. App-scoped definitions (include_tenant=false)
        // 2. ALL tenant-scoped definitions (scope=tenant)
        // This ensures we can render the "Attach" button for unattached tenant actions.
        
        const [appDefsData, tenantDefsData, attachResp] = await Promise.all([
          appName
            ? listDefinitions({ appName, region, scope: "app" })
            : Promise.resolve({ definitions: [] }),
          listDefinitions({ appName: undefined, region, scope: "tenant" }),
          appName
            ? listAttachments({
                region,
                appName,
                resourceType: "connector_definition",
              })
            : getAttachmentsByResource({
                region,
                resourceType: "connector_definition",
              }).then((map) => {
                const attachments = [];
                for (const bucket of map.values()) {
                  for (const a of bucket.attachments) attachments.push(a);
                }
                return { attachments, count: attachments.length };
              }),
        ]);

        if (cancelled) return;

        const rawApp = appDefsData.definitions || [];
        const rawTenant = tenantDefsData.definitions || [];

        setTenantDefinitions(rawTenant);
        setAppDefinitions(rawApp);

        const ids = new Set();
        const grouped = new Map();
        for (const row of attachResp.attachments || []) {
          const rid = row.tenant_resource_id;
          if (!rid) continue;
          if (!grouped.has(rid)) {
            grouped.set(rid, { app_names: [], attachments: [] });
          }
          const bucket = grouped.get(rid);
          bucket.attachments.push(row);
          if (row.app_name && !bucket.app_names.includes(row.app_name)) {
            bucket.app_names.push(row.app_name);
          }
          if (appName && row.app_name === appName) ids.add(rid);
        }
        for (const bucket of grouped.values()) bucket.app_names.sort();

        setAttachedIds(ids);
        setAttachmentsByDefinition(grouped);

        if (appName) {
          try {
            const connsData = await listConnections({ appName, region });
            if (cancelled) return;
            const providers = Array.isArray(connsData.connections)
              ? [...new Set(connsData.connections.map((c) => c.provider))]
              : [];
            setConnectedProviders(providers);
          } catch (_) {
            setConnectedProviders([]);
          }
        } else {
          setConnectedProviders([]);
        }
      } catch (err) {
        if (cancelled) return;
        openAlert({
          heading: "Couldn't load connector actions",
          paragraph: err.message,
          icon: <FaTimesCircle size={28} />,
          iconColor: "#dc2626",
          iconBG: "#fee2e2",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [appName, region, refreshTick, isGlobalContext]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!region) return;
      setLoadingOperations(true);
      try {
        const ops = await listOperations({ region });
        if (!cancelled) setOperations(ops.operations || []);
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "[ConnectorDefinitionsPanel] failed to load operations:",
            err,
          );
          setOperations([]);
        }
      } finally {
        if (!cancelled) setLoadingOperations(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [region]);

  const availableFields = useMemo(() => {
    const names = new Set(["full_name", "email", "phone", "availability"]);
    const allDefs = [...tenantDefinitions, ...appDefinitions];
    allDefs.forEach((d) => {
      Object.values(d.operation_params || {}).forEach((v) => {
        if (typeof v !== "string") return;
        const matches = v.match(/\{\{\s*([^}]+?)\s*\}\}/g) || [];
        matches.forEach((m) =>
          names.add(m.replace(/[\{\}\s]/g, "").trim()),
        );
      });
    });
    return [...names].sort();
  }, [tenantDefinitions, appDefinitions]);

  // Allow create ONLY if the user is in the correct context for the active tab.
  const canCreate =
    (activeTab === SCOPE.APP && !isGlobalContext) ||
    (activeTab === SCOPE.TENANT && isGlobalContext);

  const handleCreate = () => {
    if (!canCreate) return;
    setEditingDefinition(null);
    setModalOpen(true);
  };

  const handleEdit = (defn) => {
    if (defn.scope === SCOPE.TENANT && !isGlobalContext) {
      openAlert({
        heading: "Shared action",
        paragraph:
          "This action is published by your tenant admin. You can attach or detach it, but only the tenant admin can edit it.",
        icon: <FaInfoCircle size={28} />,
        iconColor: "#2b6cb0",
        iconBG: "#ebf8ff",
      });
      return;
    }
    setEditingDefinition(defn);
    setModalOpen(true);
  };

  const handleSaveDefinition = async (spec) => {
    try {
      const effectiveScope = activeTab === SCOPE.TENANT ? SCOPE.TENANT : SCOPE.APP;

      await createOrUpdateDefinition({
        region,
        appName: effectiveScope === SCOPE.APP ? appName : undefined,
        scope: effectiveScope,
        definitionId: editingDefinition?.definition_id,
        name: spec.name,
        operationName: spec.operation_name,
        operationParams: spec.operation_params,
      });
      setModalOpen(false);
      setEditingDefinition(null);
      setRefreshTick((t) => t + 1);
      openAlert({
        heading: editingDefinition ? "Action updated" : "Action created",
        paragraph: editingDefinition
          ? "Existing chains that reference this action will use the new configuration on their next run."
          : effectiveScope === SCOPE.TENANT
            ? "The action is now available to every chain on every app on this tenant."
            : "The action is now available to every chain on this app.",
        icon: <FaCheckCircle size={28} />,
        iconColor: "#16a34a",
        iconBG: "#dcfce7",
      });
    } catch (err) {
      const refusal = classifyGuardRefusal({ error: err.message });
      openAlert({
        heading: "Couldn't save",
        paragraph: refusal.message || err.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const performDelete = async (defn) => {
    try {
      await deleteDefinition({
        region,
        definitionId: defn.definition_id,
      });
      setRefreshTick((t) => t + 1);
      openAlert({
        heading: "Action deleted",
        paragraph: "The action has been removed from the library.",
        icon: <FaCheckCircle size={28} />,
        iconColor: "#16a34a",
        iconBG: "#dcfce7",
      });
    } catch (err) {
      if (err.blockingRefs) {
        openGuardPopup({
          refusal: {
            kind: "referenced",
            apps: [],
            refs: err.blockingRefs,
            message: err.message,
          },
        });
        return;
      }
      if (err.blockingApps) {
        openGuardPopup({
          refusal: {
            kind: "attached-in-use",
            apps: err.blockingApps,
            refs: null,
            message: err.message,
            resourceId: defn.definition_id,
          },
        });
        return;
      }
      openAlert({
        heading: "Couldn't delete",
        paragraph: formatBlockingRefs(err.blockingRefs) || err.message,
        icon: <FaExclamationTriangle size={28} />,
        iconColor: "#d97706",
        iconBG: "#fef3c7",
      });
    }
  };

  const handleDelete = (defn) => {
    openConfirm({
      heading: `Delete "${defn.name}"?`,
      subtext: `Operation: ${defn.operation_name}`,
      paragraph:
        "If any chain node or leadgen form still uses this action, " +
        "the server will refuse the delete and tell you exactly " +
        "which ones are blocking.\n\n" +
        "This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#fee2e2",
      onConfirm: async () => {
        closePopup();
        await performDelete(defn);
      },
    });
  };

  const handleAttachDefinition = async (defn) => {
    try {
      await attachScope({
        region,
        appName,
        resourceType: "connector_definition",
        tenantResourceId: defn.definition_id,
      });
      openAlert({
        heading: "Attached",
        paragraph: "This action is now available to any chain on this app.",
        icon: <FaCheckCircle size={28} />,
      });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      openAlert({
        heading: "Couldn't attach",
        paragraph: err.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDetachDefinition = (defn) => {
    openConfirm({
      heading: "Detach this action from this app?",
      subtext: `Action: ${defn.name}`,
      paragraph:
        "The action stays on the tenant — other apps that use it are " +
        "unaffected. This app will stop seeing it in the parallel-actions " +
        "picker, and any chain node that already references it will fail " +
        "on next run.",
      confirmText: "Detach",
      cancelText: "Cancel",
      icon: <FaExclamationTriangle size={28} />,
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: async () => {
        closePopup();
        try {
          await detachScope({
            region,
            appName,
            resourceType: "connector_definition",
            tenantResourceId: defn.definition_id,
          });
          openAlert({
            heading: "Detached",
            paragraph: "This app no longer has access to this action.",
            icon: <FaCheckCircle size={28} />,
          });
          setRefreshTick((t) => t + 1);
        } catch (err) {
          openAlert({
            heading: "Couldn't detach",
            paragraph: err.message,
            icon: <FaTimesCircle size={28} />,
            iconColor: "#dc2626",
            iconBG: "#fee2e2",
          });
        }
      },
    });
  };

  const renderDefinitionCard = (defn, { isTenantTab }) => {
    const bucket = attachmentsByDefinition.get(defn.definition_id);
    const appNames = bucket?.app_names || [];
    const isAttachedByMe = attachedIds.has(defn.definition_id);
    const globalAdmin = isGlobalContext;

    let badgeKind;
    if ((defn.scope || SCOPE.APP) === SCOPE.APP) {
      badgeKind = "app";
    } else if (globalAdmin) {
      badgeKind =
        appNames.length > 0 ? "tenant-detached-blocked" : "tenant-unattached";
    } else {
      badgeKind = isAttachedByMe ? "tenant-attached" : "tenant-available";
    }

    const canEdit = (defn.scope || SCOPE.APP) === SCOPE.APP || globalAdmin;

    return (
      <div key={defn.definition_id} className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.cardLine1}>
            <ResourceScopeBadge
              kind={badgeKind}
              count={
                globalAdmin && appNames.length > 0
                  ? appNames.length
                  : undefined
              }
              onClick={
                globalAdmin && appNames.length > 0
                  ? () =>
                      openGuardPopup({
                        refusal: {
                          kind: "attached-in-use",
                          apps: appNames,
                          refs: null,
                          message: `This shared action is attached to ${appNames.length} app${appNames.length === 1 ? "" : "s"}: ${appNames.join(", ")}.`,
                          resourceId: defn.definition_id,
                        },
                      })
                  : undefined
              }
            />
            <span
              className={styles.cardName}
              style={{ marginLeft: 8 }}
            >
              {defn.name}
            </span>
            <span className={styles.badgeProvider}>
              {defn.operation_name.split(".")[0]}
            </span>
          </div>
          <div className={styles.cardLine2}>
            <code>{defn.operation_name}</code>
          </div>
          <div className={styles.cardLine3}>
            {Object.keys(defn.operation_params || {}).length} parameter
            {Object.keys(defn.operation_params || {}).length === 1 ? "" : "s"}
          </div>

          {/* ⭐ Render Attach/Detach if on the Tenant tab in App Context */}
          {isTenantTab && !globalAdmin && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {isAttachedByMe ? (
                <button
                  type="button"
                  onClick={() => handleDetachDefinition(defn)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#c05621",
                    backgroundColor: "#fffaf0",
                    border: "1px solid #fbd38d",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaUnlink size={12} /> Detach from this app
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleAttachDefinition(defn)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#2b6cb0",
                    backgroundColor: "#ebf8ff",
                    border: "1px solid #90cdf4",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaLink size={12} /> Attach to this app
                </button>
              )}
            </div>
          )}
        </div>

        {canEdit && (
          <div className={styles.cardActions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => handleEdit(defn)}
              title="Edit this action"
            >
              <BiSolidEdit size={18} />
            </button>
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => handleDelete(defn)}
              title="Delete this action"
            >
              <RiDeleteBin6Line size={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingBlock}>
          <FaSyncAlt className={styles.spin} size={16} />
          <span>Loading connector actions…</span>
        </div>
      </div>
    );
  }

  const activeDefinitions =
    activeTab === SCOPE.TENANT ? tenantDefinitions : appDefinitions;
  const tenantCount = tenantDefinitions.length;
  const appCount = appDefinitions.length;
  const hasAppOverride = appDefinitions.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Connector Actions</h2>
          <p className={styles.subtitle}>
            {isGlobalContext
              ? "Reusable actions that chain nodes can fire in parallel. Configure them once at the tenant level; every app can reference them from any chain."
              : "Reusable actions that chain nodes can fire in parallel. Configure them once, reference them from any chain on this app. Editing an action updates every chain that uses it."}
          </p>
        </div>
        
        {canCreate && (
          <button
            type="button"
            className={styles.createBtn}
            onClick={handleCreate}
          >
            <FaPlus size={12} />
            <span>New action</span>
          </button>
        )}
      </div>

      {!hideScopeTabs && (
        <ScopeTabs
          activeTab={activeTab}
          onTabChange={setInternalTab}
          tenantCount={tenantCount}
          appCount={appCount}
          resourceNoun="action"
          overriddenByAppLevel={
            activeTab === SCOPE.TENANT && hasAppOverride && !isGlobalContext
          }
          attachCount={attachedIds.size}
          disabled={loading}
          compact={false}
          hiddenTabs={isGlobalContext ? ["app"] : []}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <button
          onClick={() => setRefreshTick((t) => t + 1)}
          disabled={loading}
          title="Reload actions"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12,
            color: "#475569",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FaSyncAlt size={11} /> Reload
        </button>
      </div>

      {activeDefinitions.length === 0 ? (
        <div className={styles.emptyState}>
          <p>
            {activeTab === SCOPE.TENANT
              ? isGlobalContext
                ? "No shared connector actions yet. Create one to make it available to every app on this tenant."
                : "Your tenant admin hasn't published any shared connector actions yet. Create your own from the App Level tab."
              : "No connector actions yet. Create one to make it available to any chain on this app."}
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {activeDefinitions.map((defn) =>
            renderDefinitionCard(defn, {
              isTenantTab: activeTab === SCOPE.TENANT,
            }),
          )}
        </div>
      )}

      <div className={styles.footnote}>
        <FaInfoCircle size={12} />
        <span>
          {activeTab === SCOPE.TENANT
            ? isGlobalContext
              ? "This library is shared across every app on this tenant. Deleting an action that is still referenced by any chain or leadgen form will be refused — the error will list the blocking chains and forms."
              : "Attached actions appear in this app's parallel-actions picker. Detaching an action that a chain node references will break that chain node on next run."
            : "This library is shared across every chain and leadgen form on this app. Deleting an action that is still referenced by any of them will be refused — the error will list the blocking chains and forms."}
        </span>
      </div>

      <ConnectorDefinitionModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingDefinition(null);
        }}
        onSave={handleSaveDefinition}
        operations={operations}
        loadingOperations={loadingOperations}
        editingDefinition={editingDefinition}
        connectedProviders={connectedProviders}
        availableFields={availableFields}
      />

      <ReusablePopup
        isOpen={popup.isOpen}
        onClose={closePopup}
        onConfirm={popup.onConfirm}
        icon={popup.icon}
        iconColor={popup.iconColor}
        iconBG={popup.iconBG}
        heading={popup.heading}
        subtext={popup.subtext}
        paragraph={popup.paragraph}
        cancelText={popup.cancelText}
        confirmText={popup.confirmText}
        showCancel={!!popup.cancelText}
        showConfirm={true}
      />
    </div>
  );
};

export default ConnectorDefinitionsPanel;